import React, { useReducer, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import type { FriendlyError } from '@/lib/errors';
import { getTestError } from '@/lib/errors/testErrors';
import { captureFriendlyError } from '@/lib/sentry';
import {
  startTest as apiStartTest,
  getTestQuestions,
  completeTest as apiCompleteTest,
  getTestResults,
  getTestReview,
} from '@/lib/api/test';
import type { TestQuestion, TestAnswer } from '@/lib/api/test';
import {
  TestSessionContext,
  type Answer,
  type InputMode,
  type ReviewItem,
  type TestResults,
  type TestSessionState,
} from './useTestSession';

type TestSessionAction =
  | { type: 'START_LOADING' }
  | { type: 'SET_TEST_DATA'; testAttemptId: string; questions: TestQuestion[] }
  | { type: 'SET_ERROR'; error: FriendlyError }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_ANSWER'; questionId: number; answer: Answer }
  | { type: 'SKIP_QUESTION'; questionId: number }
  | { type: 'GO_TO_QUESTION'; index: number }
  | { type: 'NEXT_QUESTION' }
  | { type: 'PREVIOUS_QUESTION' }
  | { type: 'SET_INPUT_MODE'; mode: InputMode }
  | { type: 'START_SUBMITTING' }
  | { type: 'SET_RESULTS'; results: TestResults; reviewItems: ReviewItem[] }
  | { type: 'RESET' };

const initialState: TestSessionState = {
  testAttemptId: null,
  status: 'idle',
  questions: [],
  totalQuestions: 0,
  currentIndex: 0,
  answers: new Map(),
  inputMode: 'voice',
  results: null,
  reviewItems: [],
  error: null,
};

function testSessionReducer(
  state: TestSessionState,
  action: TestSessionAction
): TestSessionState {
  switch (action.type) {
    case 'START_LOADING':
      return { ...state, status: 'loading', error: null };

    case 'SET_TEST_DATA':
      return {
        ...state,
        status: 'in_progress',
        testAttemptId: action.testAttemptId,
        questions: action.questions,
        totalQuestions: action.questions.length,
        currentIndex: 0,
        answers: new Map(),
        error: null,
      };

    case 'SET_ERROR':
      return { ...state, status: 'error', error: action.error };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
        status: state.testAttemptId ? 'in_progress' : 'idle',
      };

    case 'SET_ANSWER': {
      const newAnswers = new Map(state.answers);
      newAnswers.set(action.questionId, action.answer);
      return { ...state, answers: newAnswers };
    }

    case 'SKIP_QUESTION': {
      const newAnswers = new Map(state.answers);
      newAnswers.set(action.questionId, { userAnswer: '', skipped: true });
      return { ...state, answers: newAnswers };
    }

    case 'GO_TO_QUESTION':
      return {
        ...state,
        currentIndex: Math.max(
          0,
          Math.min(action.index, state.totalQuestions - 1)
        ),
      };

    case 'NEXT_QUESTION':
      return {
        ...state,
        currentIndex: Math.min(
          state.currentIndex + 1,
          state.totalQuestions - 1
        ),
      };

    case 'PREVIOUS_QUESTION':
      return {
        ...state,
        currentIndex: Math.max(state.currentIndex - 1, 0),
      };

    case 'SET_INPUT_MODE':
      return { ...state, inputMode: action.mode };

    case 'START_SUBMITTING':
      return { ...state, status: 'submitting', error: null };

    case 'SET_RESULTS':
      return {
        ...state,
        status: 'completed',
        results: action.results,
        reviewItems: action.reviewItems,
        error: null,
      };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

export function TestSessionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(testSessionReducer, initialState);

  const currentQuestion = useMemo(() => {
    if (state.questions.length === 0) return null;
    return state.questions[state.currentIndex] ?? null;
  }, [state.questions, state.currentIndex]);

  const currentAnswer = useMemo(() => {
    if (!currentQuestion) return null;
    return state.answers.get(currentQuestion.id) ?? null;
  }, [currentQuestion, state.answers]);

  const answeredCount = useMemo(() => state.answers.size, [state.answers]);

  const isLastQuestion = state.currentIndex === state.totalQuestions - 1;
  const isFirstQuestion = state.currentIndex === 0;

  const startTest = useCallback(async (): Promise<boolean> => {
    dispatch({ type: 'START_LOADING' });

    const startResult = await apiStartTest();
    if (!startResult.success) {
      dispatch({ type: 'SET_ERROR', error: startResult.error });
      return false;
    }

    const { test_attempt_id } = startResult.data;

    const questionsResult = await getTestQuestions(test_attempt_id);
    if (!questionsResult.success) {
      dispatch({ type: 'SET_ERROR', error: questionsResult.error });
      return false;
    }

    dispatch({
      type: 'SET_TEST_DATA',
      testAttemptId: test_attempt_id,
      questions: questionsResult.data.questions,
    });
    return true;
  }, []);

  const submitAnswer = useCallback(
    (answer: string) => {
      if (!currentQuestion) return;
      dispatch({
        type: 'SET_ANSWER',
        questionId: currentQuestion.id,
        answer: { userAnswer: answer, skipped: false },
      });
    },
    [currentQuestion]
  );

  const skipQuestion = useCallback(() => {
    if (!currentQuestion) return;
    dispatch({ type: 'SKIP_QUESTION', questionId: currentQuestion.id });
  }, [currentQuestion]);

  const nextQuestion = useCallback(() => {
    dispatch({ type: 'NEXT_QUESTION' });
  }, []);

  const previousQuestion = useCallback(() => {
    dispatch({ type: 'PREVIOUS_QUESTION' });
  }, []);

  const goToQuestion = useCallback((index: number) => {
    dispatch({ type: 'GO_TO_QUESTION', index });
  }, []);

  const setInputMode = useCallback((mode: InputMode) => {
    dispatch({ type: 'SET_INPUT_MODE', mode });
  }, []);

  const completeTest = useCallback(async (): Promise<boolean> => {
    if (!state.testAttemptId) return false;

    dispatch({ type: 'START_SUBMITTING' });

    const answers: TestAnswer[] = state.questions.map((q) => {
      const answer = state.answers.get(q.id);
      return {
        question_id: q.id,
        user_answer: answer?.userAnswer ?? '',
      };
    });

    const completeResult = await apiCompleteTest(state.testAttemptId, answers);
    if (!completeResult.success) {
      dispatch({ type: 'SET_ERROR', error: completeResult.error });
      return false;
    }

    const resultsResult = await getTestResults(state.testAttemptId);
    const reviewResult = await getTestReview(state.testAttemptId);

    if (!resultsResult.success) {
      toast.error(resultsResult.error.title, {
        description: resultsResult.error.message,
      });
    }
    if (!reviewResult.success) {
      toast.error(reviewResult.error.title, {
        description: reviewResult.error.message,
      });
    }

    const results: TestResults = {
      totalQuestions: completeResult.data.total_questions,
      correctAnswers: completeResult.data.correct_answers,
      scorePercentage: completeResult.data.score_percentage,
      passed: completeResult.data.passed,
    };

    if (resultsResult.success) {
      results.timeTakenMinutes = resultsResult.data.summary.time_taken_minutes;
      results.weakCategories = resultsResult.data.summary.weak_categories;
    }

    let reviewItems: ReviewItem[] = [];
    if (reviewResult.success) {
      try {
        reviewItems = reviewResult.data.question_attempts.map((attempt) => ({
          questionId: attempt.question_id,
          questionText: attempt.question.question_text,
          category: attempt.question.category,
          userAnswer: attempt.user_answer,
          isCorrect: attempt.is_correct,
          correctAnswer: attempt.question.answer_config?.accepted_answers?.[0] ?? '',
          explanation: attempt.question.explanation,
        }));
      } catch (err) {
        captureFriendlyError(err, getTestError('review_load_failed'), {
          testAttemptId: state.testAttemptId,
        });
        reviewItems = [];
      }
    }

    dispatch({ type: 'SET_RESULTS', results, reviewItems });
    return true;
  }, [state.testAttemptId, state.questions, state.answers]);

  const resetTest = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const retryLastAction = useCallback(async () => {
    if (state.status === 'error') {
      if (!state.testAttemptId) {
        await startTest();
      } else if (state.results === null) {
        await completeTest();
      }
    }
  }, [state.status, state.testAttemptId, state.results, startTest, completeTest]);

  const contextValue = useMemo(
    () => ({
      ...state,
      currentQuestion,
      currentAnswer,
      answeredCount,
      isLastQuestion,
      isFirstQuestion,
      startTest,
      submitAnswer,
      skipQuestion,
      nextQuestion,
      previousQuestion,
      goToQuestion,
      setInputMode,
      completeTest,
      resetTest,
      clearError,
      retryLastAction,
    }),
    [
      state,
      currentQuestion,
      currentAnswer,
      answeredCount,
      isLastQuestion,
      isFirstQuestion,
      startTest,
      submitAnswer,
      skipQuestion,
      nextQuestion,
      previousQuestion,
      goToQuestion,
      setInputMode,
      completeTest,
      resetTest,
      clearError,
      retryLastAction,
    ]
  );

  return (
    <TestSessionContext.Provider value={contextValue}>
      {children}
    </TestSessionContext.Provider>
  );
}
